import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  fetchFarmerCards,
  fetchFarmersLayer,
  fetchGeoServer,
  fetchOutlineLayer,
} from '../../api/api';
import type {
  FarmerCardsCollection,
  PlantingZonesCollection,
  TreeExtentsCollection,
} from '../../api/schemas';
import { plantingZonesCollectionSchema, treeExtentsCollectionSchema } from '../../api/schemas';
import { fetchTreePinsByProject, type TreePinCollection } from '../../api/trees';

/*
 * The get*Query helpers below are custom query hooks by behaviour but named
 * get* (not use*) per this file's long-standing convention. react-hooks/
 * rules-of-hooks fires on the naming alone - every useQuery call here is
 * unconditional and only ever runs inside a component. Renaming each hook plus
 * its call sites would be an out-of-scope refactor (§7), so the rule is disabled
 * file-wide to match the established pattern rather than partially.
 */
/* eslint-disable react-hooks/rules-of-hooks */

interface CRS {
  properties: { name: string };
  type: string;
}

interface Geometry {
  type: string;
  coordinates: any;
}

export interface Feature {
  properties: any;
  geometry: Geometry;
  // Optional since 2026-09-18: the validated tree-pin features (src/api/trees.ts)
  // flow through this same type, and their schema does not promise either field.
  geometry_name?: string;
  id?: string | number;
  type: string;
}

export interface Layer {
  crs: CRS;
  features: Feature[];
  numberMatched: number;
  numberReturned: number;
  timeStamp: string;
  totalFeatures: number;
  type: string;
}

const staleTime = 60000; // 1 min
const cacheTime = 300000; // 5 min

// farmer_cards_public: 5 cohort rows. Drives the headline farmer total
// (SUM(farmer_count) = 13,391) and the cohort cards. Validated at the boundary.
export const getFarmerCardsQuery = (
  options?: UseQueryOptions<FarmerCardsCollection, Error>,
): UseQueryResult<FarmerCardsCollection, Error> => {
  return useQuery<FarmerCardsCollection, Error>(['FarmerCardsPublic'], () => fetchFarmerCards(), {
    staleTime,
    cacheTime,
    ...options,
  });
};

// key_impact_data_live also carries labels_json (founder decision 2026-09-06:
// dashboard wording is data, read from this row by useLabels()) - no extra
// request for labels.
export const getImpactQuery = (
  params?: Record<string, unknown>,
  options?: UseQueryOptions<Layer, Error>,
): UseQueryResult<Layer, Error> => {
  return useQuery<Layer, Error>(
    ['ImpactLayer', params],
    () => fetchGeoServer<Layer>(import.meta.env.VITE_IMPACT_LAYER, params),
    {
      staleTime,
      cacheTime,
      ...options,
    },
  );
};

export const getFarmersGroupQuery = (
  params?: Record<string, unknown>,
  options?: UseQueryOptions<Layer, Error>,
): UseQueryResult<Layer, Error> => {
  return useQuery<Layer, Error>(
    ['FarmersGroups', params],
    () => fetchGeoServer<Layer>(import.meta.env.VITE_FARMERS_GROUP, params),
    {
      staleTime,
      cacheTime,
      ...options,
    },
  );
};

export const getForestsGroupQuery = (
  params?: Record<string, unknown>,
  options?: UseQueryOptions<Layer, Error>,
): UseQueryResult<Layer, Error> => {
  return useQuery<Layer, Error>(
    ['ForestsGroups', params],
    () => fetchGeoServer<Layer>(import.meta.env.VITE_FORESTS_GROUP, params),
    {
      staleTime,
      cacheTime,
      ...options,
    },
  );
};

export const getFarmersQuery = (
  params?: Record<string, unknown>,
  options?: UseQueryOptions<Layer, Error>,
): UseQueryResult<Layer, Error> => {
  return useQuery<Layer, Error>(['FarmLayer', params], () => fetchFarmersLayer<Layer>(params), {
    staleTime,
    cacheTime,
    ...options,
  });
};

export const getFactoriesQuery = (
  params?: Record<string, unknown>,
  options?: UseQueryOptions<Layer, Error>,
): UseQueryResult<Layer, Error> => {
  return useQuery<Layer, Error>(
    ['FactoriesLayer', params],
    () => fetchGeoServer<Layer>(import.meta.env.VITE_FACTORIES_LAYER, params),
    {
      staleTime,
      cacheTime,
      ...options,
    },
  );
};

// Founder decision 2026-09-17: the forest map is scoped to Mizan only. Only
// this project's row of geonode:tree_project_extents is requested and drawn.
// Previously (deprecated in place, §2) the whole layer was fetched unfiltered.
export const MIZAN_EXTENT_PROJECT = 'Mizan 1 Ingrowers';

// D16a - forest outline layers. retry: 1 (not the default 3): an unpublished
// layer or schema failure should reach its honest no-outline state quickly,
// same reasoning as the D10 news retry decision. Errors log the layer name;
// consumers render nothing on error/undefined (fail-visible, §10) - the map
// must never crash because an outline layer is missing.
export const getTreeExtentsQuery = (
  options?: UseQueryOptions<TreeExtentsCollection, Error>,
): UseQueryResult<TreeExtentsCollection, Error> => {
  return useQuery<TreeExtentsCollection, Error>(
    ['TreeProjectExtents', MIZAN_EXTENT_PROJECT],
    async () => {
      const data = await fetchOutlineLayer(
        'tree_project_extents',
        treeExtentsCollectionSchema,
        `project='${MIZAN_EXTENT_PROJECT}'`,
      );
      // Belt and braces: if upstream ever ignores CQL_FILTER, other projects'
      // extents must still never be drawn.
      return {
        ...data,
        features: data.features.filter((f) => f.properties.project === MIZAN_EXTENT_PROJECT),
      };
    },
    {
      staleTime,
      cacheTime,
      retry: 1,
      onError: (e) =>
        console.warn(
          '[outline] tree_project_extents unavailable — no computed extents drawn:',
          e.message,
        ),
      ...options,
    },
  );
};

export const getPlantingZonesQuery = (
  typeName: 'mizan1zones' | 'mizan2_zone_data_public',
  options?: UseQueryOptions<PlantingZonesCollection, Error>,
): UseQueryResult<PlantingZonesCollection, Error> => {
  return useQuery<PlantingZonesCollection, Error>(
    ['PlantingZones', typeName],
    () => fetchOutlineLayer(typeName, plantingZonesCollectionSchema),
    {
      staleTime,
      cacheTime,
      retry: 1,
      onError: (e) =>
        console.warn(`[outline] ${typeName} unavailable — zone polygons not drawn:`, e.message),
      ...options,
    },
  );
};

// Founder decision 2026-09-18 (Option 2): pins are fetched per project, four
// parallel requests of 2,500 each (same 10,000 total budget as before), each
// filtered to photographed rows and validated row by row at the boundary.
// See src/api/trees.ts. The browser-side "photo starts with http" check in
// Dashboard.tsx is kept: upstream stores '' (not NULL) for missing photos, so
// the CQL filter alone lets those rows through.
export const getTreesQuery = (
  options?: UseQueryOptions<TreePinCollection, Error>,
): UseQueryResult<TreePinCollection, Error> => {
  return useQuery<TreePinCollection, Error>(
    ['TreesPlantedLayer', 'per-project'],
    fetchTreePinsByProject,
    {
      staleTime,
      cacheTime,
      onError: (e) => console.warn('[trees] tree pins unavailable - none drawn:', e.message),
      ...options,
    },
  );
};

// Deprecated in place 2026-09-18 (§2): the single unfiltered request through
// VITE_TREES_PLANTED_LAYER (maxFeatures=10000, no CQL filter, no schema). Its
// select() string-parse fallback is no longer needed because the boundary
// now validates. Superseded by getTreesQuery above.
// export const getTreesQuery = (
//   params?: Record<string, unknown>,
//   options?: UseQueryOptions<Layer, Error>,
// ): UseQueryResult<Layer, Error> => {
//   return useQuery<Layer, Error>(
//     ['TreesPlantedLayer', params],
//     () => fetchGeoServer<Layer>(import.meta.env.VITE_TREES_PLANTED_LAYER, params),
//     {
//       staleTime,
//       cacheTime,
//       select: (data) => {
//         if (typeof data === 'string') {
//           try {
//             return JSON.parse(data) as Layer;
//           } catch {
//             return data as unknown as Layer;
//           }
//         }
//         return data;
//       },
//       ...options,
//     },
//   );
// };
